/**
 * Companio content pages (policy documents, FAQ page, Team & Bulk).
 *
 * One asset serves all three custom elements so the pages share a single
 * cached file:
 * - scrollspy for the sticky section-nav (policy + FAQ page)
 * - accordion for hidden-attribute FAQ items (FAQ page + Team & Bulk) —
 *   distinct from the homepage's grid-rows accordion in companio-faq.js
 * - "Copy answer link" with clipboard + inline confirmation (FAQ page)
 * - deep linking: #question-id opens that item on load (FAQ page + bulk FAQ)
 * - quote jump buttons + fixed mobile quote bar (Team & Bulk); the bar shows
 *   once the hero is scrolled past and hides while the form is on screen
 * - light client-side validation mirroring the prototype's .has-error
 *   treatment (Team & Bulk quote form)
 */

/* ---------- shared helpers ---------- */

function companioScrollspy(root) {
  const nav = root.querySelector('[data-section-nav]');
  if (!nav) return;
  const links = Array.from(nav.querySelectorAll('a[href^="#"]'));
  const targets = links
    .map((link) => root.querySelector(link.hash))
    .filter(Boolean);
  if (!targets.length) return;

  const activate = (id) => {
    links.forEach((link) => link.classList.toggle('active', link.hash === `#${id}`));
  };

  const observer = new IntersectionObserver(
    (entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible.length) activate(visible[0].target.id);
    },
    { rootMargin: '-96px 0px -60% 0px' }
  );

  targets.forEach((target) => observer.observe(target));
  activate(targets[0].id);
}

function companioAccordion(root) {
  root.addEventListener('click', (event) => {
    const button = event.target.closest('.faq-question');
    if (!button || !root.contains(button)) return;
    const item = button.closest('.faq-item');
    const answer = item && item.querySelector('.faq-answer');
    if (!answer) return;
    const open = answer.hidden;
    answer.hidden = !open;
    button.setAttribute('aria-expanded', String(open));
  });
}

function companioOpenFromHash(root) {
  const openItem = () => {
    if (!window.location.hash) return;
    let item;
    try {
      item = root.querySelector(`.faq-item${window.location.hash}`);
    } catch (error) {
      return;
    }
    if (!item) return;
    const button = item.querySelector('.faq-question');
    const answer = item.querySelector('.faq-answer');
    if (answer) answer.hidden = false;
    if (button) button.setAttribute('aria-expanded', 'true');
    item.scrollIntoView({ block: 'start' });
  };
  openItem();
  window.addEventListener('hashchange', openItem);
}

/* ---------- policy documents ---------- */

class CompanioPolicy extends HTMLElement {
  connectedCallback() {
    companioScrollspy(this);
  }
}

/* ---------- FAQ page ---------- */

class CompanioFaqPage extends HTMLElement {
  connectedCallback() {
    companioScrollspy(this);
    companioAccordion(this);
    companioOpenFromHash(this);

    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-copy-link]');
      if (!button) return;
      const url = `${window.location.origin}${window.location.pathname}${button.dataset.copyLink}`;
      const confirm = () => {
        const original = button.textContent;
        button.textContent = 'Link copied';
        window.setTimeout(() => {
          button.textContent = original;
        }, 1600);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(confirm, confirm);
      } else {
        window.prompt('Copy this link:', url);
      }
    });
  }
}

/* ---------- Team & Bulk ---------- */

class CompanioTeamBulk extends HTMLElement {
  connectedCallback() {
    companioAccordion(this);
    companioOpenFromHash(this);
    this.setUpQuoteJump();
    this.setUpMobileBar();
    this.setUpValidation();

    // After a successful post, Shopify reloads with #contact_form (or our
    // anchor); make sure the confirmation is on screen and announced.
    const success = this.querySelector('[data-quote-success]');
    if (success) {
      success.scrollIntoView({ block: 'center' });
      success.focus({ preventScroll: true });
    }
  }

  setUpQuoteJump() {
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-quote-jump]');
      if (!button) return;
      const form = this.querySelector('.quote-section');
      if (form) form.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  setUpMobileBar() {
    const bar = this.querySelector('[data-mobile-quote-bar]');
    const hero = this.querySelector('.bulk-hero');
    const quote = this.querySelector('.quote-section');
    if (!bar || !hero || !quote) return;

    let pastHero = false;
    let quoteVisible = false;
    const update = () => bar.classList.toggle('visible', pastHero && !quoteVisible);

    new IntersectionObserver((entries) => {
      pastHero = !entries[0].isIntersecting;
      update();
    }).observe(hero);

    new IntersectionObserver((entries) => {
      quoteVisible = entries[0].isIntersecting;
      update();
    }).observe(quote);
  }

  setUpValidation() {
    const form = this.querySelector('.bulk-quote-form');
    if (!form) return;

    const validateField = (input) => {
      const field = input.closest('.form-field');
      if (!field) return true;
      const valid = input.checkValidity();
      field.classList.toggle('has-error', !valid);
      return valid;
    };

    form.addEventListener('submit', (event) => {
      const inputs = Array.from(form.querySelectorAll('[required]'));
      const firstInvalid = inputs.filter((input) => !validateField(input))[0];
      if (firstInvalid) {
        event.preventDefault();
        firstInvalid.focus();
      }
    });

    form.addEventListener(
      'blur',
      (event) => {
        if (event.target.matches && event.target.matches('[required]')) validateField(event.target);
      },
      true
    );
  }
}

/* ---------- Gifts page ---------- */

class CompanioGifts extends HTMLElement {
  connectedCallback() {
    this.modal = this.querySelector('[data-gift-box-modal]');
    if (!this.modal) return;

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-gift-box-open]')) return this.toggleModal(true);
      if (event.target.closest('[data-gift-box-close]')) return this.toggleModal(false);
      if (event.target === this.modal) this.toggleModal(false);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.modal.classList.contains('open')) this.toggleModal(false);
    });
  }

  toggleModal(open) {
    this.modal.classList.toggle('open', open);
    document.body.classList.toggle('modal-open', open);
    if (open) this.modal.querySelector('.modal-close')?.focus();
  }
}

/* ---------- contact page ---------- */

class CompanioContact extends HTMLElement {
  connectedCallback() {
    const form = this.querySelector('.companio-contact-form');
    if (!form) return;

    // "Photo issue" reveals the replacement-photo note.
    const subject = form.querySelector('[data-contact-subject]');
    const help = form.querySelector('[data-photo-help]');
    if (subject && help) {
      subject.addEventListener('change', () => {
        help.classList.toggle('show', subject.value === 'Photo issue');
      });
    }

    // Client-side validation with the prototype's inline state banner.
    const banner = this.querySelector('[data-contact-validation]');
    const validateField = (input) => {
      const valid = input.checkValidity();
      const message = input.closest('.companio-field')?.querySelector('.field-message');
      if (message) message.classList.toggle('show', !valid);
      if (valid) {
        input.removeAttribute('aria-invalid');
      } else {
        input.setAttribute('aria-invalid', 'true');
      }
      return valid;
    };

    form.addEventListener('submit', (event) => {
      const invalid = Array.from(form.querySelectorAll('[required]')).filter(
        (input) => !validateField(input)
      );
      if (banner) banner.classList.toggle('show', invalid.length > 0);
      if (invalid.length) {
        event.preventDefault();
        invalid[0].focus();
      }
    });

    const success = this.querySelector('[data-contact-success]');
    if (success) {
      success.scrollIntoView({ block: 'center' });
      success.focus({ preventScroll: true });
    }
  }
}

/* ---------- customer login (recover-password toggle) ---------- */

class CompanioAuth extends HTMLElement {
  connectedCallback() {
    const auth = this.querySelector('[data-auth]');
    if (!auth) return;
    const apply = () => {
      auth.dataset.mode = window.location.hash === '#recover' ? 'recover' : 'login';
    };
    apply();
    window.addEventListener('hashchange', apply);
    // A failed recover POST reloads with the recover form's errors — keep the
    // recover card visible so the message isn't hidden behind the login card.
    if (auth.querySelector('.auth-recover .auth-errors, .auth-recover [role="status"]')) {
      auth.dataset.mode = 'recover';
    }
  }
}

/* ---------- address book ---------- */

class CompanioAddresses extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      const add = event.target.closest('[data-address-add]');
      const edit = event.target.closest('[data-address-edit]');
      const cancel = event.target.closest('[data-address-cancel]');
      if (add) this.openForm('new');
      if (edit) this.openForm(edit.dataset.addressEdit);
      if (cancel) this.closeForms();
    });

    this.querySelectorAll('[data-address-delete]').forEach((form) => {
      form.addEventListener('submit', (event) => {
        if (!window.confirm(form.dataset.confirm || 'Remove this address?')) {
          event.preventDefault();
        }
      });
    });

    // Country → province pairing, using the option data Shopify embeds in
    // all_country_option_tags.
    this.querySelectorAll('[data-address-country]').forEach((country) => {
      const wrap = country.closest('.field-row-account');
      const province = wrap && wrap.querySelector('[data-address-province]');
      if (!province) return;

      const sync = () => {
        const selected = country.options[country.selectedIndex];
        let provinces = [];
        try {
          provinces = JSON.parse(selected?.dataset.provinces || '[]');
        } catch (error) {
          provinces = [];
        }
        province.innerHTML = '';
        provinces.forEach(([value, label]) => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = label;
          if (value === province.dataset.default) option.selected = true;
          province.appendChild(option);
        });
        const provinceField = province.closest('[data-province-wrap]');
        if (provinceField) provinceField.style.display = provinces.length ? '' : 'none';
      };

      if (country.dataset.default) country.value = country.dataset.default;
      country.addEventListener('change', sync);
      sync();
    });
  }

  openForm(id) {
    this.closeForms();
    const form = this.querySelector(`[data-address-form="${CSS.escape(id)}"]`);
    if (form) {
      form.classList.add('open');
      form.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  closeForms() {
    this.querySelectorAll('.address-form-card.open').forEach((card) => card.classList.remove('open'));
  }
}

if (!customElements.get('companio-gifts')) {
  customElements.define('companio-gifts', CompanioGifts);
}
if (!customElements.get('companio-contact')) {
  customElements.define('companio-contact', CompanioContact);
}
if (!customElements.get('companio-auth')) {
  customElements.define('companio-auth', CompanioAuth);
}
if (!customElements.get('companio-addresses')) {
  customElements.define('companio-addresses', CompanioAddresses);
}
if (!customElements.get('companio-policy')) {
  customElements.define('companio-policy', CompanioPolicy);
}
if (!customElements.get('companio-faq-page')) {
  customElements.define('companio-faq-page', CompanioFaqPage);
}
if (!customElements.get('companio-team-bulk')) {
  customElements.define('companio-team-bulk', CompanioTeamBulk);
}
